import type { AlertBannerCardProps } from '../../../types/alerts.d';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertBannerCard } from '/react4xp/shared/AlertBannerCard/AlertBannerCard';
import { NoActiveAlerts } from '/react4xp/shared/NoActiveAlerts/NoActiveAlerts';


export function ActiveAlerts({
	alertContentId,
	activeAlerts,
	activeAlertsCount,
}: {
	alertContentId: string | null;
	activeAlerts: AlertBannerCardProps[];
	activeAlertsCount: number;
}): React.JSX.Element {
	// ─────────────────────────────────────────────────────────────────────────────────────────────
	// Refs
	// ─────────────────────────────────────────────────────────────────────────────────────────────
	const activeAlertsContainerRef = useRef<HTMLDivElement | null>(null);
	const highlightRef = useRef<HTMLDivElement | null>(null);

	// ─────────────────────────────────────────────────────────────────────────────────────────────
	// State
	// ─────────────────────────────────────────────────────────────────────────────────────────────
	const [queryId, setQueryId] = useState<string | null>(alertContentId);

	// ─────────────────────────────────────────────────────────────────────────────────────────────
	// Memos
	// ─────────────────────────────────────────────────────────────────────────────────────────────

	// Memoize alerts to avoid unnecessary re-renders
	const modifiedActiveAlerts = useMemo(() => {
		return activeAlerts.map(alert => ({
			...alert,
			isHighlighted: queryId === alert.id,
		}));
	}, [activeAlerts, queryId]);

	// ─────────────────────────────────────────────────────────────────────────────────────────────
	// Effects
	// ─────────────────────────────────────────────────────────────────────────────────────────────

	// Extract query parameter on mount & when URL changes
	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		setQueryId(params.get("alertId"));
	}, []);

	// Scroll to the highlighted alert when queryId updates and after alert list load in DOM
	useEffect(() => {
		if (!highlightRef.current || !activeAlertsContainerRef.current) return;

		const observer = new MutationObserver(() => {
			if (highlightRef.current) {
				const elementTop = highlightRef.current.getBoundingClientRect().top + window.scrollY;
				const offset = 16; // Adjust offset as needed

				window.scrollTo({
					top: elementTop - offset,
					behavior: "smooth",
				});

				observer.disconnect(); // Stop observing after scrolling
			}
		});

		observer.observe(activeAlertsContainerRef.current, { childList: true, subtree: true });

		// Focus then remove focus for accessibility smooth scroll
		highlightRef.current.focus();
		highlightRef.current.blur();

		return () => observer.disconnect();
	}, [queryId]);

	return (
		<div ref={activeAlertsContainerRef} className="flex flex-col gap-md tablet:gap-lg">
			{activeAlertsCount === 0 ? (
				<NoActiveAlerts isActive={true} />
			) : (
				modifiedActiveAlerts && modifiedActiveAlerts.map((alert) => (
					<AlertBannerCard
						alertListLink={alert.alertListLink}
						content={alert.content}
						date={alert.date}
						id={alert.id}
						image={alert.image}
						isHighlighted={alert.isHighlighted}
						key={alert.id}
						location={alert.location}
						ref={alert.id === queryId ? highlightRef : null}
						title={alert.title}
						variant={alert.variant}
						alertType={alert.alertType}
					/>
				))
			)}
		</div>
	);
}
